import { VisuallyHidden } from '@radix-ui/themes';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { forwardRef } from 'react';
import styles from './styles.module.css';

/**
 * TODO:
 * overlay requires modal: true
 *
 */

interface RootProps extends ComponentProps<typeof DialogPrimitive.Root> {
  overlay?: boolean;
  children?: ReactNode;
}
const HubertDialog = ({ overlay, children, ...props }: RootProps) => {
  return (
    <DialogPrimitive.Root
      {...props}
      // open={Layout.show}
      // modal={false}
    >
      {overlay ? <DialogPrimitive.Overlay className={clsx(styles.DialogOverlay)} /> : null}

      {children}
    </DialogPrimitive.Root>
  );
};

interface TriggerProps extends ComponentProps<typeof DialogPrimitive.Trigger> {
  className?: string;
  children: ReactNode;
}
function Trigger({ className, children, ...props }: TriggerProps) {
  return (
    <DialogPrimitive.Trigger {...props} className={clsx(className)} asChild>
      {children}
    </DialogPrimitive.Trigger>
  );
}

interface TitleProps extends ComponentProps<typeof DialogPrimitive.Title> {
  children: ReactNode;
}

function Title({ children, ...props }: TitleProps) {
  return (
    <DialogPrimitive.Title
      {...props}
      // asChild
    >
      {children}
    </DialogPrimitive.Title>
  );
}

// @ts-ignore
interface ContentContainerProps extends ComponentProps<typeof DialogPrimitive.Content> {
  title?: ReactNode;
  description?: ReactNode;
  closeButton?: boolean;
  closeFunction?: () => void;
  className?: string;
  position: 'left' | 'right' | 'top' | 'bottom' | 'center';
  children: ReactNode;
  portalContent: boolean;
  portalElement?: HTMLElement;
}

function ContentContainer({
  title,
  description,
  closeButton,
  children,
  className = '',
  position = 'center',
  portalContent = true,
  portalElement,
}: ContentContainerProps) {
  if (portalContent) {
    return (
      <DialogPrimitive.Portal container={portalElement}>
        <Content
          title={title}
          description={description}
          closeButton={closeButton}
          className={className}
          position={position}
        >
          {children}
        </Content>
      </DialogPrimitive.Portal>
    );
  }
  return (
    <Content
      title={title}
      description={description}
      closeButton={closeButton}
      className={className}
      position={position}
    >
      {children}
    </Content>
  );
}

// @ts-ignore
interface ContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  title?: ReactNode;
  description?: ReactNode;
  closeButton?: boolean;
  closeFunction?: () => void;
  className?: string;
  position: 'left' | 'right' | 'top' | 'bottom' | 'center';
  children: ReactNode;
}

const Content = forwardRef<HTMLDivElement, ContentProps>(
  (
    { title = '', description, closeButton, closeFunction, children, className = '', position = 'center', ...props },
    ref,
  ) => {
    let positionStyles;
    switch (position) {
      case 'center':
        positionStyles = styles.DialogCenter;
        break;
      case 'right':
        positionStyles = styles.DialogRight;
        break;
      case 'left':
        positionStyles = styles.DialogLeft;
        break;
      case 'bottom':
        positionStyles = styles.DialogBottom;
        break;
      case 'top':
        positionStyles = styles.DialogTop;
        break;
      default:
        positionStyles = styles.DialogCenter;
        break;
    }

    return (
      <DialogPrimitive.Content
        {...props}
        ref={ref}
        className={clsx(
          styles.DialogContent,
          positionStyles,
          'bg-white',
          'p-[24px]',
          'shadow-current',
          'shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)]',
          'will-change-[transform,opacity]',
          'z-10',
          'data-[state=open]:data-[side=top]:animate-slideDownAndFade',
          'data-[state=open]:data-[side=right]:animate-slideLeftAndFade',
          'data-[state=open]:data-[side=bottom]:animate-slideUpAndFade',
          'data-[state=open]:data-[side=left]:animate-slideRightAndFade',
          className,
        )}
      >
        {title ? (
          <DialogPrimitive.Title className="">{title}</DialogPrimitive.Title>
        ) : (
          <VisuallyHidden>
            <DialogPrimitive.Title aria-hidden="true" />
          </VisuallyHidden>
        )}
        {description ? <DialogPrimitive.Description className="">{description}</DialogPrimitive.Description> : null}
        {children}
        {closeButton ? (
          <DialogPrimitive.Close
            className={clsx(
              'absolute top-[5px] right-[5px]',
              'inline-flex items-center justify-center',
              'h-[25px] w-[25px]',
              'cursor-default outline-none',
              'rounded-full',
              'text-violet11 hover:bg-violet4',
              'cursor-pointer',
              // 'focus:shadow-[0_0_0_2px] focus:shadow-violet7'
            )}
            onClick={() => {
              if (closeButton && closeFunction) {
                closeFunction();
              }
            }}
            aria-label="Close"
          >
            <X />
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    );
  },
);

Content.displayName = 'Content';

HubertDialog.Portal = DialogPrimitive.Portal;
HubertDialog.Trigger = Trigger;
HubertDialog.Content = ContentContainer;
HubertDialog.Title = Title;
HubertDialog.Close = DialogPrimitive.Close;
export default HubertDialog;
